import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
  Bold, Italic, List, ListOrdered, Quote, Minus,
  Heading1, Heading2, Heading3, Link2, Image as ImageIcon,
} from "lucide-react";

export default function RichEditor({ value, onChange, placeholder = "Начните писать…", onInsertImageClick }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false, code: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noreferrer" } }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Sync external `value` when a new material is loaded into the editor.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) editor.commands.setContent(value || "");
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL:");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const addImage = () => {
    if (onInsertImageClick) {
      onInsertImageClick((url) => editor.chain().focus().setImage({ src: url }).run());
      return;
    }
    const url = window.prompt("URL изображения:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const B = ({ onClick, active, children, title, testid }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 rounded-lg transition-colors ${
        active ? "bg-espresso text-white" : "text-espresso hover:bg-sand/50"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="tiptap-editor rounded-2xl border border-input bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-input/60 px-2 py-1.5 bg-cream/60">
        <B onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="H1" testid="rt-h1"><Heading1 className="w-4 h-4" /></B>
        <B onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="H2" testid="rt-h2"><Heading2 className="w-4 h-4" /></B>
        <B onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="H3" testid="rt-h3"><Heading3 className="w-4 h-4" /></B>
        <div className="w-px h-5 bg-input mx-1" />
        <B onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Жирный" testid="rt-bold"><Bold className="w-4 h-4" /></B>
        <B onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Курсив" testid="rt-italic"><Italic className="w-4 h-4" /></B>
        <div className="w-px h-5 bg-input mx-1" />
        <B onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Список" testid="rt-ul"><List className="w-4 h-4" /></B>
        <B onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Нумерованный" testid="rt-ol"><ListOrdered className="w-4 h-4" /></B>
        <B onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Цитата" testid="rt-quote"><Quote className="w-4 h-4" /></B>
        <B onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Разделитель" testid="rt-hr"><Minus className="w-4 h-4" /></B>
        <div className="w-px h-5 bg-input mx-1" />
        <B onClick={addLink} active={editor.isActive("link")} title="Ссылка" testid="rt-link"><Link2 className="w-4 h-4" /></B>
        <B onClick={addImage} title="Изображение" testid="rt-image"><ImageIcon className="w-4 h-4" /></B>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
